import type { ChargeLimits, ChinaPaymentType } from '@mypoz/shared';

/**
 * What the new app offers for one China Payment request type (MYP-109).
 *
 * - `submit` — whether the request can be sent from here (`ChargeResult`). Off, the card shows the
 *   type with a link to the same form on mypoz.com instead.
 * - `maxRmbCents` — the largest amount accepted here; larger amounts go to mypoz.com. Set where the
 *   vendor's quote (`alipay_rechange`) is known to be wrong above it, so the app never shows a
 *   customer a total that is not what staff will expect.
 */
export interface ChinaPaymentTypeRules extends ChargeLimits {
  readonly submit: boolean;
}

/**
 * Per type, checked against the old website on 22 Oct 2026 (China Payment API issues, #27):
 *
 * - **Top-ups** (Alipay, WeChat): the API's quote is right under ¥500 (¥160 → RM 111.59, as the
 *   old site), but from ¥500 it charges no fee where the old site charges 3% (¥1,111: RM 705.40
 *   instead of RM 726.56). The app can add the 3% itself (`withTopUpFee`), but `ChargeResult` is
 *   sent the RMB amount only, so the server may still record its own lower total — a customer
 *   would transfer RM 726.56 for a request staff see as RM 705.40. So top-ups stop at ¥499.99
 *   (decided 23 Oct, review of #56) until the vendor fixes the quote, or a live ¥500+ request
 *   shows the server records the old site's total; then remove `maxRmbCents` (one line each) and
 *   `withTopUpFee` prices them.
 * - **Proxy pay**: the API quotes it with the top-up fee (RM 10 / 3%) instead of RM 20 / 5% at every
 *   amount, and the Taobao password question is still open. Off.
 * - **Bank transfer**: the quote matches the old site (¥160 → RM 111.11, ¥1,000 → RM 644.44). On.
 */
export const CHINA_PAYMENT_TYPE_RULES: Readonly<Record<ChinaPaymentType, ChinaPaymentTypeRules>> = {
  ALIPAYCHARGE: { submit: true, maxRmbCents: 49_999 },
  WXCHARGE: { submit: true, maxRmbCents: 49_999 },
  ALIPAY: { submit: false },
  BANKTRANS: { submit: true },
};

/** Top-ups without the rate card's fee rule: the API's quote is only right under ¥500. */
export const TOP_UP_MAX_WITHOUT_FEE_RULE_RMB_CENTS = 49_999;
